import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Paper, Typography, TextField, Button, Stack, Divider, CircularProgress } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LinkIcon from "@mui/icons-material/Link";
import { useSelector } from "react-redux";
import { selectWorkshopDetails } from "../../../app/store/userSlice";
import toast from "react-hot-toast";
import SaveIcon from "@mui/icons-material/Save";
import { useUpdateWorkshopMutation } from "../../../app/store/api/workshopApi";
import ConnectErp from "./ConnectErp";

const QueueDays = () => {
  const { t } = useTranslation();
  const workshopDetails = useSelector(selectWorkshopDetails);
  const [updateWorkshop, { isLoading: saving }] = useUpdateWorkshopMutation();
  const [queueDays, setQueueDays] = useState(3);
  const [ openDialogErp, setOpenDialogErp ] = useState(false);

  useEffect(() => {
    if (workshopDetails?.queueSize != null) {
      setQueueDays(workshopDetails.queueSize);
    }
  }, [workshopDetails]);

  const parsedDays = parseInt(queueDays, 10);
  const isValid = Number.isInteger(parsedDays) && parsedDays >= 1;
  const isDirty = parsedDays !== workshopDetails?.queueSize;

  const closeDialogErp = () => {
    setOpenDialogErp(false);
  };

  const openDialogErpHandler = () => {
    setOpenDialogErp(true);
  };

  const handleSave = async () => {
    if (!isValid) return;
    const payload = { queueSize: parsedDays };
    toast.promise(
      async () => {
        await updateWorkshop({ workshopId: workshopDetails.id, payload }).unwrap();
      },
      {
        success: t("toastMessage.saveSuccess"),
        loading: t("toastMessage.saveLoading"),
        error: t("toastMessage.saveError"),
      },
    );
  };

  return (
    <>
    <Paper variant='outlined' sx={{ borderColor: "#e3e6ec", borderRadius: 2, p: 3, mb: 4 }}>
      <Stack direction='row' spacing={1} alignItems='flex-start' sx={{ mb: 2 }}>
        <Box sx={{ color: "#00C9DB", mt: "2px" }}>
          <AccessTimeIcon fontSize='small' />
        </Box>
        <Typography variant='subtitle1' sx={{ fontWeight: 700, color: "#003C59", lineHeight: 1.3 }}>
          {t("workshopQueue.title")}
        </Typography>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <Typography variant='body2' sx={{ fontWeight: 400, color: "text.primary", lineHeight: 1.3 }}>
          {t("workshopQueue.description")}
        </Typography>
      </Box>

      <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 2 }}>
        <TextField type='number' size='small' value={queueDays} onChange={(e) => setQueueDays(e.target.value)} error={!isValid} inputProps={{ min: 1, step: 1 }} sx={{ width: 90 }} />
        <Typography variant='body2' sx={{ fontWeight: 400, color: "text.primary", lineHeight: 1.3 }}>
          {t("workshopQueue.days")}
        </Typography>
        <Button
          variant='contained'
          size='small'
          onClick={handleSave}
          disabled={!isValid || !isDirty || saving}
          startIcon={saving ? <CircularProgress size={14} color='inherit' /> : <SaveIcon fontSize='small' />}
          sx={{ bgcolor: "#003C59", "&:hover": { bgcolor: "#003C59" }, whiteSpace: "nowrap" }}
        >
          {t("workshopSettings.save")}
        </Button>
      </Stack>

      <Box
        sx={{
          bgcolor: "#f7f8fa",
          border: `1px solid ${"#e3e6ec"}`,
          borderRadius: 1.5,
          p: 2,
          mb: 3,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant={"caption"} sx={{ fontWeight: 400, color: "text.primary", lineHeight: 1.3, mb: 0.5 }}>
            {t("workshopQueue.subTitle")}
          </Typography>
        </Box>
        <Typography sx={{ fontWeight: 600, color: "#003C59" }}>{t("workshopQueue.subDescription", { queueDays })}</Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={2}>
        <Box sx={{ mb: 0.5 }}>
          <Typography variant={"body1"} sx={{ fontWeight: 600, color: "text.primary", lineHeight: 1.3 }}>
            {t("workshopQueue.erpIntegration")}
          </Typography>
        </Box>
  <Button variant='outlined' onClick={openDialogErpHandler} size='small' startIcon={<LinkIcon fontSize='small' />} sx={{ borderColor: "#e3e6ec", color: "#003C59", whiteSpace: "nowrap" }}>
          {t("workshopQueue.connectERP")}
        </Button>
      </Stack>
      <Box sx={{ mb: 2 }}>
        <Typography variant={"body2"} sx={{ fontWeight: 400, color: "text.primary", lineHeight: 1.3 }}>
          {t("workshopQueue.erpIntegrationDescription")}
        </Typography>
      </Box>
    </Paper>

    {openDialogErp && <ConnectErp onClose={closeDialogErp} open={openDialogErp} />}

    </>
  );
};

export default QueueDays;
